import { useCallback, useEffect, useState } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ApiError, api } from './api';
import type { SessionUser, SignedInUser } from './api';
import { ChangePasswordDialog, ChooseNewPassword } from './ChangePassword';
import { PlainLanguageProvider } from './plainLanguage';
import { SearchDialog } from './Search';
import { WEBHOOK_ALERTS_PATH, WebhookAlertBanner } from './WebhookAlerts';
import { MENU_ITEM, Menu } from './ui';
import { Login } from './pages/Login';
import { Organizations } from './pages/Organizations';
import { OrganizationDetail } from './pages/OrganizationDetail';
import { OutletDetail } from './pages/OutletDetail';
import { Activity } from './pages/Activity';
import { StaffUsers } from './pages/StaffUsers';
import { WebhookFailures } from './pages/WebhookFailures';

type Session = { state: 'loading' } | { state: 'out' } | { state: 'in'; user: SignedInUser };

export function App() {
  const [session, setSession] = useState<Session>({ state: 'loading' });

  const check = useCallback(() => {
    api.auth
      .me()
      .then((user) => setSession({ state: 'in', user }))
      .catch((cause: unknown) => {
        // A 401 is the normal "not signed in" answer, not a failure worth reporting.
        if (cause instanceof ApiError && cause.isUnauthenticated) {
          setSession({ state: 'out' });
          return;
        }
        setSession({ state: 'out' });
      });
  }, []);

  useEffect(check, [check]);

  if (session.state === 'loading') {
    return <div className="grid min-h-screen place-items-center p-6 text-muted">Checking your session…</div>;
  }

  if (session.state === 'out') {
    return <Login onSignedIn={(user) => setSession({ state: 'in', user })} />;
  }

  // Signed in with a temporary password: the server refuses everything else until a new
  // one is chosen, so this page stands in for the whole console. Re-checking afterwards
  // reads the cleared flag back from the server rather than assuming it.
  if (session.user.mustChangePassword) {
    return (
      <ChooseNewPassword
        user={session.user}
        onChanged={check}
        onSignOut={() => void api.auth.logout().finally(() => setSession({ state: 'out' }))}
      />
    );
  }

  // A merchant's login gets the same pages in the shop's words (plainLanguage.tsx).
  return (
    <PlainLanguageProvider value={session.user.role !== 'staff'}>
      <Shell user={session.user} onSignedOut={() => setSession({ state: 'out' })} />
    </PlainLanguageProvider>
  );
}

/**
 * A top bar rather than a side rail.
 *
 * Once navigation followed the data — organization > outlet > counters, with tabs
 * inside an organization — the sidebar had one link in it for staff and duplicated
 * the tabs for everyone else. Breadcrumbs and tabs do the navigating now, so the
 * chrome shrinks to a bar: where you work, and who you are signed in as.
 *
 * Below md the bar swaps to its narrow form — a section dropdown on the left, search
 * and account on the right — rather than wrapping. Wrapping is what the first version
 * did: 111px of sticky bar on an 812px screen, in two lines that each held one item
 * and left a hole beside it.
 */
function Shell({ user, onSignedOut }: { user: SessionUser; onSignedOut: () => void }) {
  const isStaff = user.role === 'staff';
  const [searchOpen, setSearchOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const { pathname } = useLocation();

  // A customer login has exactly one organization and is dropped straight inside it;
  // a list of one teaches nothing.
  const home = isStaff ? '/organizations' : `/organizations/${user.orgId}/outlets`;

  // The narrow bar names the section on the dropdown itself, the way the links name it
  // on a wide one — otherwise the only thing on screen saying where you are is a menu
  // you have to open to read.
  const section = pathname.startsWith('/staff')
    ? 'Staff'
    : pathname.startsWith('/activity')
      ? 'Activity'
      : 'Organizations';

  const signOut = () => {
    void api.auth.logout().finally(onSignedOut);
  };

  // Ctrl/Cmd+K, the shortcut people already try. preventDefault because Firefox and
  // Safari give it to their own address bar otherwise.
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setSearchOpen(true);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-20 flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-divider bg-raised px-4 py-2.5 sm:px-5 lg:gap-x-[18px] lg:px-8">
        <NavLink
          to={home}
          className="flex shrink-0 items-center gap-2 leading-[1.15] text-text no-underline"
        >
          {/* The companion's icon, cropped to its drawing (icon-mark.png). Decorative - the
              link's text names it - and kept in a narrow bar, unlike the product name,
              because it takes almost no room. */}
          <img src="/icon-mark.png" alt="" width={18} height={32} className="h-8 w-auto" />
          <span>
            {/* The product name is on the tab and the sign-in page; in a narrow bar it
                is a second line that says nothing about where you are. */}
            <small className="block text-[10px] uppercase tracking-[0.08em] text-muted max-md:hidden">
              MPOSMobilePay
            </small>
            <strong>Admin</strong>
          </span>
        </NavLink>

        {/* Staff work across customers, so they get the two top-level places. A
            customer login is always inside its own organization already, and gets
            neither the links nor the dropdown that replaces them. */}
        {isStaff && (
          <>
            <nav className="flex gap-1 max-md:hidden">
              <TopLink to="/organizations" label="Organizations" />
              <TopLink to="/staff" label="Staff" />
              <TopLink to="/activity" label="Activity" />
            </nav>

            <Menu
              className="md:hidden"
              buttonClassName="flex min-w-0 cursor-pointer items-center gap-1.5 rounded-lg px-2 py-1.5 font-semibold text-text hover:bg-hover"
              align="start"
              label={
                <>
                  {section}
                  <Chevron />
                </>
              }
            >
              <MenuLink to="/organizations">Organizations</MenuLink>
              <MenuLink to="/staff">Staff</MenuLink>
              <MenuLink to="/activity">Activity</MenuLink>
            </Menu>
          </>
        )}

        <div className="ml-auto flex min-w-0 items-center gap-3">
          <button
            onClick={() => setSearchOpen(true)}
            className="flex min-w-[150px] cursor-pointer items-center gap-2 rounded-lg border border-divider bg-input px-2.5 py-1.5 text-muted hover:border-accent hover:text-text max-md:hidden lg:min-w-[210px]"
          >
            <SearchIcon />
            Search
            <span className="ml-auto rounded border border-divider px-1.5 py-px text-[11px] max-lg:hidden">
              Ctrl K
            </span>
          </button>

          <div className="flex items-center gap-3 max-md:hidden">
            {/* Who you are is also where your own settings live. */}
            <Menu
              buttonClassName="flex min-w-0 cursor-pointer flex-col items-end rounded-lg px-2 py-1 text-xs leading-[1.35] text-text hover:bg-hover"
              ariaLabel="Account"
              align="end"
              label={
                <>
                  <span className="max-w-[30ch] truncate">{user.email}</span>
                  <span className="max-w-[30ch] truncate text-muted">{roleLabel(user.role)}</span>
                </>
              }
            >
              <button onClick={() => setPasswordOpen(true)} className={MENU_ITEM}>
                Change password
              </button>
            </Menu>
            <button
              onClick={signOut}
              className="cursor-pointer rounded-lg bg-sunken px-4 py-2 font-semibold whitespace-nowrap text-text transition-colors hover:bg-hover"
            >
              Sign out
            </button>
          </div>

          {/* The narrow-screen pair: a search launcher, then the account. One set or
              the other is shown, never both. */}
          <button onClick={() => setSearchOpen(true)} aria-label="Search" className={`${ICON} md:hidden`}>
            <SearchIcon />
          </button>

          <Menu
            className="md:hidden"
            buttonClassName={`${ICON} bg-accent font-semibold text-white`}
            ariaLabel="Account"
            align="end"
            label={user.email.slice(0, 1).toUpperCase()}
          >
            <div className="mb-1.5 border-b border-divider px-2.5 pt-1.5 pb-2.5 text-xs">
              <strong className="block truncate">{user.email}</strong>
              <span className="text-muted">{roleLabel(user.role)}</span>
            </div>
            {/*
              Sign out sits behind a tap here rather than in the bar. There is no room
              for it beside a truncated email, and it is an easy thing to catch by
              mistake for something that ends the session.
            */}
            <button onClick={() => setPasswordOpen(true)} className={MENU_ITEM}>
              Change password
            </button>
            <button onClick={signOut} className={`${MENU_ITEM} text-red-300`}>
              Sign out
            </button>
          </Menu>
        </div>
      </header>

      <SearchDialog open={searchOpen} onClose={() => setSearchOpen(false)} />

      <ChangePasswordDialog open={passwordOpen} onClose={() => setPasswordOpen(false)} />

      <main className="mx-auto w-full min-w-0 max-w-[1180px] px-4 pt-4 pb-12 sm:px-5 sm:pt-5 sm:pb-14 lg:px-8 lg:pt-7 lg:pb-16">
        {/* Staff only: a customer login could not act on it, and would see others' failures. */}
        {isStaff && <WebhookAlertBanner />}
        <Routes>
          <Route path="/organizations" element={<Organizations user={user} />} />
          <Route path="/staff" element={<StaffUsers user={user} />} />
          {isStaff && <Route path="/activity" element={<Activity />} />}
          {isStaff && <Route path={WEBHOOK_ALERTS_PATH} element={<WebhookFailures />} />}
          <Route
            path="/organizations/:orgId/outlets/:outletId"
            element={<OutletDetail user={user} />}
          />
          <Route path="/organizations/:orgId/:tab" element={<OrganizationDetail user={user} />} />
          <Route path="/organizations/:orgId" element={<OrganizationDetail user={user} />} />
          <Route path="*" element={<Navigate to={home} replace />} />
        </Routes>
      </main>
    </div>
  );
}

/** The round tap target the narrow bar uses in place of a labelled control. */
const ICON =
  'inline-flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-full bg-sunken text-text';

function TopLink({ to, label }: { to: string; label: string }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `rounded-lg px-3 py-1.5 whitespace-nowrap no-underline ${
          isActive ? 'bg-accent text-white' : 'text-muted hover:bg-hover hover:text-text'
        }`
      }
    >
      {label}
    </NavLink>
  );
}

function MenuLink({ to, children }: { to: string; children: string }) {
  return (
    <NavLink to={to} className={({ isActive }) => `${MENU_ITEM} ${isActive ? 'text-accent' : ''}`}>
      {children}
    </NavLink>
  );
}

/* Inline rather than an icon font or a package: two shapes, drawn once, themed by
   currentColor and with nothing to keep up to date. */

function SearchIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="M15.5 15.5 21 21" />
    </svg>
  );
}

function Chevron() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function roleLabel(role: SessionUser['role']): string {
  switch (role) {
    case 'staff':
      return 'Sorting Company staff';
    case 'customer_admin':
      return 'Administrator';
  }
}
